import React, { useEffect, useMemo, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  FlaskConical,
  Package as PackageIcon,
  RefreshCw,
  LogOut,
  Inbox,
  Clock,
  UserPlus,
  CheckCircle2,
  Check,
  AlertCircle,
  ClipboardCheck,
  Eye,
  Wrench,
  User as UserIcon,
  Pause,
  Play,
  ShieldCheck,
  Send,
  History,
  X,
  Search,
} from 'lucide-react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import PackageScanner from '../../components/PackageScanner';
import { useTranslation } from 'react-i18next';
import { hasTask } from '../../utils/tasks';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Page shell with sidebar ─────────────────────────────────────────
const Shell = styled.div`
  min-height: 100vh;
  display: grid;
  grid-template-columns: 240px 1fr;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 100%);
`;

const Sidebar = styled.aside`
  background: #0f172a;
  color: #e2e8f0;
  padding: 1.25rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  position: sticky;
  top: 0;
  height: 100vh;

  .brand {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.25rem 0.4rem 1rem;
    border-bottom: 1px solid #1e293b;
    margin-bottom: 0.25rem;
  }
  .brand .icon {
    width: 36px; height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
    display: flex; align-items: center; justify-content: center;
    color: white;
  }
  .brand h1 { margin: 0; font-size: 1rem; font-weight: 800; color: white; }
  .brand p { margin: 0; font-size: 0.7rem; color: #94a3b8; }

  .nav { display: flex; flex-direction: column; gap: 0.2rem; }

  .me {
    margin-top: auto;
    padding: 0.6rem 0.5rem;
    border-top: 1px solid #1e293b;
  display: flex;
    align-items: center;
    gap: 0.55rem;
    font-size: 0.78rem;
    color: #cbd5e1;

    .av {
      width: 30px; height: 30px;
      border-radius: 50%;
      background: #6366f1;
      color: white;
      display: flex; align-items: center; justify-content: center;
      font-weight: 700;
    }
    .role { color: #64748b; font-size: 0.7rem; }
    .logout {
      margin-left: auto;
      background: transparent;
      border: none;
      color: #f87171;
      cursor: pointer;
      padding: 4px;
      border-radius: 6px;
    }
    .logout:hover { background: rgba(239, 68, 68, 0.1); }
  }
`;

const NavItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 0.65rem;
  border-radius: 9px;
  border: none;
  background: ${(p) => (p.active ? 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)' : 'transparent')};
  color: ${(p) => (p.active ? 'white' : '#cbd5e1')};
  font-size: 0.84rem;
  font-weight: ${(p) => (p.active ? 700 : 500)};
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${(p) => (p.active ? '' : '#1e293b')};
    color: white;
  }

  .badge {
    margin-left: auto;
    background: ${(p) => (p.active ? 'rgba(255,255,255,0.25)' : '#1e293b')};
    color: ${(p) => (p.active ? 'white' : '#94a3b8')};
    padding: 1px 7px;
    border-radius: 999px;
    font-size: 0.7rem;
    font-weight: 700;
    min-width: 22px;
    text-align: center;
  }
`;

const Main = styled.main`
  padding: 1.5rem 2rem 3rem;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  gap: 1rem;
  flex-wrap: wrap;
  animation: ${fadeIn} 0.25s ease;

  h2 { margin: 0; font-size: 1.4rem; font-weight: 800; color: #0f172a; }
  p  { margin: 4px 0 0; font-size: 0.85rem; color: #64748b; max-width: 640px; }

  .right {
    display: flex; gap: 0.5rem; align-items: center;
  }
`;

const SearchBar = styled.div`
  position: relative;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.7rem;
  min-width: 240px;

  input {
    border: none;
    outline: none;
    background: transparent;
    width: 100%;
    font-size: 0.85rem;
  }
`;

const Card = styled.section`
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 1.1rem 1.2rem;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  animation: ${fadeIn} 0.25s ease;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h3`
  margin: 0 0 0.75rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Empty = styled.div`
  padding: 1.25rem;
  text-align: center;
  color: #94a3b8;
  font-style: italic;
`;

const StatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.125rem 0.55rem;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
  background: ${(p) => p.bg || '#e0e7ef'};
  color: ${(p) => p.color || '#475569'};
`;

const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.9rem;
  border-radius: 9px;
  border: 1px solid #e2e8f0;
  background: white;
  color: #334155;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  &:hover:not(:disabled) { background: #f1f5f9; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

// Pass and fail are a tick and a cross, nothing else. The pair is glanced at
// dozens of times a shift and the shapes read faster than words — the label
// lives in title/aria-label so it stays reachable and screen-readable.
const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 120ms cubic-bezier(0.23, 1, 0.32, 1), background 160ms ease;

  ${(p) => (p.$tone === 'pass'
    ? 'background:#dcfce7; border:1px solid #86efac; color:#166534;'
    : 'background:#fee2e2; border:1px solid #fca5a5; color:#991b1b;')}

  &:hover:not(:disabled) {
    ${(p) => (p.$tone === 'pass' ? 'background:#bbf7d0;' : 'background:#fecaca;')}
  }
  &:active:not(:disabled) { transform: scale(0.94); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.55rem 1rem;
  border-radius: 9px;
  border: none;
  background: ${(p) => p.bg || 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)'};
  color: white;
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const Toast = styled.div`
  background: ${(p) => (p.kind === 'success' ? '#dcfce7' : '#fee2e2')};
  color: ${(p) => (p.kind === 'success' ? '#166534' : '#991b1b')};
  border: 1px solid ${(p) => (p.kind === 'success' ? '#bbf7d0' : '#fecaca')};
  border-radius: 10px;
  padding: 0.55rem 0.8rem;
  font-size: 0.85rem;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

// ── Modal for hold reason ──────────────────────────────────────────
const ModalBackdrop = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex; align-items: center; justify-content: center;
  z-index: 100;
  padding: 1rem;
`;
const ModalShell = styled.div`
  background: white;
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  padding: 1.25rem 1.4rem 1.1rem;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
  animation: ${fadeIn} 0.2s ease;

  .head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.6rem; }
  h3 { margin: 0; font-size: 1.05rem; font-weight: 800; color: #0f172a; }
  label { font-size: 0.82rem; color: #475569; font-weight: 600; display: block; margin-bottom: 0.35rem; }
  select, textarea {
    width: 100%;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    padding: 0.55rem 0.7rem;
    font-size: 0.88rem;
    font-family: inherit;
    background: white;
    color: #0f172a;
  }
  textarea { resize: vertical; min-height: 70px; }
  .row { display: flex; flex-direction: column; gap: 0.6rem; margin-bottom: 0.85rem; }
  .actions { display: flex; gap: 0.5rem; justify-content: flex-end; }
`;

// ── Hold reasons (matching server constants) ────────────────────────
const HOLD_REASON_KEYS = {
  lens_broken: 'lab.holdLensBroken',
  frame_broken: 'lab.holdFrameBroken',
  machine_malfunction: 'lab.holdMachineMalfunction',
  missing_part: 'lab.holdMissingPart',
  waiting_supplier: 'lab.holdWaitingSupplier',
  rework_needed: 'lab.holdReworkNeeded',
  other: 'lab.holdOtherFreeText',
};

const formatDateTime = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString(); } catch (_) { return ''; }
};

const tLabOrderStatus = (tr, status) => {
  if (!status) return '';
  const key = String(status).toLowerCase().replace(/\s+/g, '_');
  const translated = tr(`orderStatus.${key}`, { defaultValue: '' });
  return translated || String(status).replace(/_/g, ' ');
};

// Tiny shared component for an order's prescription / item summary.
const OrderDetails = ({ order }) => {
  const { t: tr } = useTranslation();
  const customer = order.visitor
    ? [order.visitor.firstName, order.visitor.lastName].filter(Boolean).join(' ') || tr('lab.customer')
    : tr('lab.customer');
  const items = (order.items || [])
    .filter((it) => it.itemType === 'lens' || it.itemType === 'frame')
    .map((it) => `${it.itemType === 'lens' ? 'Lens' : 'Frame'}: ${it.name || '—'}`);
  const rx = order.prescription || null;
  const renderEye = (label, eye) => {
    if (!eye) return null;
    const parts = [];
    if (eye.sphere != null && eye.sphere !== '') parts.push(`SPH ${eye.sphere}`);
    if (eye.cylinder != null && eye.cylinder !== '') parts.push(`CYL ${eye.cylinder}`);
    if (eye.axis != null && eye.axis !== '') parts.push(`AXE ${eye.axis}°`);
    if (eye.add != null && eye.add !== '' && Number(eye.add) !== 0) parts.push(`ADD ${eye.add}`);
    if (eye.pd != null && eye.pd !== '') parts.push(`PD ${eye.pd}`);
    if (!parts.length) return null;
    return (
      <div key={label} style={{ display: 'flex', gap: '0.4rem', alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, color: '#475569', minWidth: 24 }}>{label}</span>
        <span style={{ color: '#0f172a', fontSize: '0.85rem' }}>{parts.join(' · ')}</span>
      </div>
    );
  };
  return (
    <>
      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', color: '#475569', fontSize: '0.85rem' }}>
        <span><strong style={{ color: '#0f172a' }}>{customer}</strong></span>
        {order.store?.name && <span>{tr('lab.from')} {order.store.name}</span>}
        {order.salesPerson?.firstName && (
          <span>{tr('lab.sales')} {order.salesPerson.firstName} {order.salesPerson.lastName || ''}</span>
        )}
      </div>
      {items.length > 0 && (
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '0.5rem 0.75rem',
          fontSize: '0.85rem',
          color: '#334155',
        }}>
          {items.map((line, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <PackageIcon size={12} color="#64748b" />
              <span>{line}</span>
            </div>
          ))}
        </div>
      )}
      {rx && (
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '0.55rem 0.75rem',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.35rem' }}>
            <Eye size={13} color="#6366f1" />
            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.8rem' }}>
              {tr('lab.prescription')}{rx.prescriptionType ? ` · ${String(rx.prescriptionType).replace('_', ' ')}` : ''}
            </span>
          </div>
          <div style={{ display: 'grid', gap: '0.2rem' }}>
            {renderEye('OD', rx.rightEye)}
            {renderEye('OS', rx.leftEye)}
            {!rx.rightEye && !rx.leftEye && (
              <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: '0.82rem' }}>
                {tr('lab.noPrescriptionDetailsOnFile')}
              </span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

// ── Main component ──────────────────────────────────────────────────
const LabDashboard = () => {
  const { t: tr } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; }
    catch { return {}; }
  }, []);
  // Task switches: "Lab management" (receive, assign, control, send back) and
  // "Lab work" (waiting, active, recent). Admins have both.
  const isLabManager = hasTask(user, 'lab.manage');
  const doesLabWork = hasTask(user, 'lab.work');
  const meId = String(user.id || user._id || '');

  // Who may sign a job off. Orders assigned before controllers existed name
  // nobody, so the lab manager keeps the old ability there — but never on their
  // own work, which the server enforces too.
  const isController = useCallback((order) => {
    const builtByMe = String(order?.technician?._id || order?.technician_id || '') === meId;
    if (builtByMe) return false;
    if (order?.controllerId) return String(order.controllerId) === meId;
    return user.role === 'lab_manager' || user.role === 'admin';
  }, [meId, user.role]);

  // ── Server state ─────────────────────────────────────────────────
  const [waiting, setWaiting] = useState([]);     // status='in_transit_to_lab'
  const [inbound, setInbound] = useState([]);     // status='at_lab', no technician
  const [active, setActive] = useState([]);       // technician=me, status manufacturing/qc
  const [onHold, setOnHold] = useState([]);       // every paused order in lab
  const [qc, setQc] = useState([]);               // status='quality_check'
  const [recent, setRecent] = useState([]);       // recently shipped out of lab
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  // ── UI state ─────────────────────────────────────────────────────
  const [tab, setTab] = useState(() => {
    const initialTab = new URLSearchParams(window.location.search).get('tab');
    return initialTab || 'waiting';
  });
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState({});           // orderId → bool
  const [notice, setNotice] = useState(null);     // global { kind, message }
  const [pickedTech, setPickedTech] = useState({}); // orderId → techId
  const [pickedController, setPickedController] = useState({}); // orderId → controllerId
  const [holdModal, setHoldModal] = useState(null);  // { order } | null
  const [holdReasonCode, setHoldReasonCode] = useState('lens_broken');
  const [holdNotes, setHoldNotes] = useState('');
  const [qcModal, setQcModal] = useState(null);   // { order, outcome }
  const [qcNotes, setQcNotes] = useState('');

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get('/api/orders/lab/dashboard', { headers });
      setWaiting(data?.waiting || []);
      setInbound(data?.inbound || []);
      setActive(data?.active || []);
      setOnHold(data?.onHold || []);
      setQc(data?.qc || []);
      setRecent(data?.recent || []);
      setTechnicians(data?.technicians || []);
    } catch (err) {
      console.error('Failed to load lab data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll, refreshKey]);

  useEffect(() => {
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (requestedTab) setTab(requestedTab);
  }, [location.search]);

  useEffect(() => {
    const interval = setInterval(() => {
      fetchAll();
    }, 20000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const setBusyFor = (id, val) => setBusy((m) => ({ ...m, [id]: val }));

  // ── Actions ──────────────────────────────────────────────────────
  const receiveOrder = useCallback(async (order) => {
    setBusyFor(order._id, true);
    setNotice(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        '/api/orders/lab/receive',
        { code: order.orderNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const tail = data?.packageAdvanced ? ` Package ${data.packageNumber} fully received.` : '';
      setNotice({ kind: 'success', message: `Order ${order.orderNumber} received.${tail}` });
      refresh();
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to mark received' });
    } finally {
      setBusyFor(order._id, false);
    }
  }, []);

  const assignOrder = useCallback(async (orderId, technicianId, controllerId) => {
    if (!technicianId || !controllerId) {
      setNotice({ kind: 'error', message: 'Pick who works on it and who controls it.' });
      return;
    }
    if (String(technicianId) === String(controllerId)) {
      setNotice({ kind: 'error', message: 'The control has to be a different person from the one doing the work.' });
      return;
    }
    setBusyFor(orderId, true);
    setNotice(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/orders/${orderId}/assign-technician`,
        { technicianId, controllerId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotice({ kind: 'success', message: 'Order assigned.' });
      refresh();
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to assign' });
    } finally {
      setBusyFor(orderId, false);
    }
  }, []);

  // Lab manager / technician marks an order complete on the bench →
  // hands it to QC (status `quality_check`).
  const markComplete = useCallback(async (order) => {
    setBusyFor(order._id, true);
    setNotice(null);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `/api/orders/${order._id}/status`,
        { status: 'quality_check', notes: 'Lens completed — handing to control' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotice({
        kind: 'success',
        message: `Order ${order.orderNumber} sent for control.`,
      });
      refresh();
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to mark complete' });
    } finally {
      setBusyFor(order._id, false);
    }
  }, []);

  const submitHold = async () => {
    if (!holdModal?.order) return;
    const order = holdModal.order;
    setBusyFor(order._id, true);
    setNotice(null);
    try {
      const token = localStorage.getItem('token');
      const reasonLabel = HOLD_REASON_KEYS[holdReasonCode]
        ? tr(HOLD_REASON_KEYS[holdReasonCode])
        : tr('lab.holdOtherFreeText');
      await axios.post(
        `/api/orders/${order._id}/hold`,
        {
          reasonCode: holdReasonCode,
          reason: reasonLabel,
          notes: holdNotes || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotice({ kind: 'success', message: tr('lab.orderPaused', { number: order.orderNumber }) });
      setHoldModal(null);
      setHoldNotes('');
      setHoldReasonCode('lens_broken');
      refresh();
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to pause' });
    } finally {
      setBusyFor(order._id, false);
    }
  };

  const releaseHold = useCallback(async (order) => {
    setBusyFor(order._id, true);
    setNotice(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/orders/${order._id}/release`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotice({ kind: 'success', message: `Order ${order.orderNumber} resumed.` });
      refresh();
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to resume' });
    } finally {
      setBusyFor(order._id, false);
    }
  }, []);

  const submitQc = async () => {
    if (!qcModal?.order) return;
    const order = qcModal.order;
    const outcome = qcModal.outcome;
    setBusyFor(order._id, true);
    setNotice(null);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `/api/orders/${order._id}/qc`,
        { outcome, notes: qcNotes || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotice({
        kind: 'success',
        message: outcome === 'pass'
          ? tr('lab.orderPassedQc', { number: order.orderNumber })
          : tr('lab.orderBouncedToManufacturing', { number: order.orderNumber }),
      });
      setQcModal(null);
      setQcNotes('');
      refresh();
    } catch (err) {
      setNotice({ kind: 'error', message: err.response?.data?.message || 'Failed to record QC' });
    } finally {
      setBusyFor(order._id, false);
    }
  };

  // ── Filtering helpers ────────────────────────────────────────────
  const matchSearch = useCallback((o) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    const pieces = [
      o.orderNumber,
      o.visitor?.firstName,
      o.visitor?.lastName,
      o.store?.name,
      o.salesPerson?.firstName,
      o.salesPerson?.lastName,
    ].filter(Boolean).map(String).map((s) => s.toLowerCase());
    return pieces.some((s) => s.includes(q));
  }, [search]);

  const counts = {
    waiting: waiting.length,
    inbound: inbound.length,
    active: active.length,
    on_hold: onHold.length,
    qc: qc.length,
    recent: recent.length,
  };

  // QC-pass orders that are ready for the manager to scan/handoff.
  const handoffReady = useMemo(
    () => qc.filter((o) => {
      const last = (o.qcHistory || []).slice(-1)[0];
      return last && last.outcome === 'pass';
    }),
    [qc]
  );

  // ── Render helpers ───────────────────────────────────────────────
  const renderActiveCard = (o) => {
    const isMine = String(o.technician?._id || o.technician || '') === meId;
    const onHoldNow = !!o.onHold;
    const isQc = o.status === 'quality_check';
    return (
      <div
        key={o._id}
        style={{
          border: `1px solid ${onHoldNow ? '#fde68a' : '#c7d2fe'}`,
          borderRadius: 14,
          background: onHoldNow
            ? 'linear-gradient(180deg, #fffbeb 0%, #ffffff 100%)'
            : 'linear-gradient(180deg, #eef2ff 0%, #ffffff 100%)',
          padding: '0.85rem 1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.55rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>{o.orderNumber}</strong>
            {onHoldNow ? (
              <StatusPill bg="#fef3c7" color="#92400e"><Pause size={11} /> {tr('lab.onHold')}</StatusPill>
            ) : (
              <StatusPill bg={isQc ? '#fef9c3' : '#ede9fe'} color={isQc ? '#854d0e' : '#5b21b6'}>
                <Wrench size={11} /> {isQc ? tr('lab.control') : tr('lab.inProgress')}
              </StatusPill>
            )}
            {isMine && <StatusPill bg="#dbeafe" color="#1e40af"><UserIcon size={11} /> {tr('lab.you')}</StatusPill>}
            {!isMine && o.technician?.firstName && (
              <StatusPill bg="#e2e8f0" color="#334155">
                <UserIcon size={11} /> {o.technician.firstName} {o.technician.lastName || ''}
              </StatusPill>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
            {onHoldNow ? (
              <PrimaryButton onClick={() => releaseHold(o)} disabled={busy[o._id]} bg="linear-gradient(135deg, #10b981 0%, #059669 100%)">
                <Play size={13} /> {busy[o._id] ? tr('lab.resuming') : tr('lab.resume')}
              </PrimaryButton>
            ) : (
              <>
                <GhostButton onClick={() => { setHoldModal({ order: o }); setHoldReasonCode('lens_broken'); setHoldNotes(''); }} disabled={busy[o._id]}>
                  <Pause size={13} /> {tr('lab.pause')}
                </GhostButton>
                {/* Finishing is the assignee's call. A lab manager looking at
                    someone else's bench sees who it belongs to instead of a
                    button they are not allowed to press. */}
                {!isQc && (String(o.technician?._id || o.technician_id || '') === meId ? (
                  <PrimaryButton onClick={() => markComplete(o)} disabled={busy[o._id]} bg="linear-gradient(135deg, #10b981 0%, #059669 100%)">
                    <CheckCircle2 size={13} /> {busy[o._id] ? tr('lab.saving') : tr('lab.markComplete')}
                  </PrimaryButton>
                ) : (
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                    {o.technician?.firstName
                      ? `${o.technician.firstName} marks this complete`
                      : tr('lab.onlyTheAssigneeCanMark')}
                  </span>
                ))}
              </>
            )}
          </div>
        </div>
        {onHoldNow && o.holdReason && (
          <div style={{
            background: '#fef3c7',
            border: '1px solid #fde68a',
            color: '#854d0e',
            borderRadius: 10,
            padding: '0.45rem 0.7rem',
            fontSize: '0.82rem',
          }}>
            <strong>{tr('lab.reason')}</strong> {o.holdReason}
            {o.heldAt && <span style={{ color: '#a16207' }}> {tr('lab.paused')} {formatDateTime(o.heldAt)}</span>}
          </div>
        )}
        <OrderDetails order={o} />
      </div>
    );
  };

  // ── Tabs ─────────────────────────────────────────────────────────
  const TABS = [
    { id: 'waiting', label: tr('lab.tabWaiting'), icon: Inbox,         count: counts.waiting,  manager: false },
    { id: 'receive', label: tr('lab.tabReceive'), icon: ClipboardCheck, count: null,           manager: true  },
    { id: 'assign',  label: tr('lab.tabAssign'),  icon: UserPlus,      count: counts.inbound,  manager: true  },
    { id: 'active',  label: tr('lab.tabActive'),  icon: Wrench,        count: counts.active,   manager: false },
    { id: 'on_hold', label: tr('lab.tabOnHold'),  icon: Pause,         count: counts.on_hold,  manager: false },
    { id: 'qc',      label: tr('lab.tabControl'), icon: ShieldCheck,   count: counts.qc,       manager: true  },
    { id: 'handoff', label: tr('lab.tabHandoff'), icon: Send,          count: handoffReady.length, manager: true },
    { id: 'recent',  label: tr('lab.tabRecent'),  icon: History,       count: counts.recent,   manager: false },
  ];

  const visibleTabs = TABS.filter((t) => (t.manager ? isLabManager : doesLabWork));

  // ── Tab bodies ───────────────────────────────────────────────────
  const renderWaitingTab = () => (
    <Card>
      <SectionTitle><Inbox size={16} /> {tr('lab.ordersWaitingToBeReceived')}</SectionTitle>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
        {tr('lab.centralJustOpenedAPackage')}
      </div>
      {waiting.filter(matchSearch).length === 0 ? (
        <Empty>{tr('lab.nothingWaitingCentralHasnT')}</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          {waiting.filter(matchSearch).map((o) => {
            const customer = o.visitor
              ? [o.visitor.firstName, o.visitor.lastName].filter(Boolean).join(' ') || tr('lab.customer')
              : tr('lab.customer');
            const sales = o.salesPerson ? [o.salesPerson.firstName, o.salesPerson.lastName].filter(Boolean).join(' ') : '';
            const itemSummary = (o.items || []).slice(0, 3).map((it) => it.name || it.itemType).filter(Boolean).join(', ');
            return (
              <div
                key={o._id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem',
                  padding: '0.7rem 0.85rem', border: '1px solid #fde68a',
                  borderRadius: 12, background: '#fffbeb', flexWrap: 'wrap',
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: '0.15rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>{o.orderNumber}</strong>
                    <StatusPill bg="#fef3c7" color="#92400e"><Clock size={11} /> {tr('lab.waiting')}</StatusPill>
                    {o.package?.packageNumber && (
                      <StatusPill bg="#e0e7ff" color="#3730a3"><PackageIcon size={11} /> {o.package.packageNumber}</StatusPill>
                    )}
                  </div>
                  <span style={{ color: '#475569', fontSize: '0.85rem' }}>{customer}</span>
                  <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                    {o.store?.name ? `From ${o.store.name}` : ''}
                    {sales ? ` · sales ${sales}` : ''}
                    {itemSummary ? ` · ${itemSummary}` : ''}
                  </span>
                </div>
                {isLabManager && (
                  <PrimaryButton onClick={() => receiveOrder(o)} disabled={busy[o._id]} bg="linear-gradient(135deg, #16a34a 0%, #15803d 100%)">
                    <CheckCircle2 size={13} /> {busy[o._id] ? tr('lab.receiving') : tr('lab.markReceived')}
                  </PrimaryButton>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  const renderReceiveTab = () => (
    <>
      <Card>
        <SectionTitle><ClipboardCheck size={16} /> {tr('lab.receiveAnIndividualOrder')}</SectionTitle>
        <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.7rem' }}>
          {tr('lab.centralHandsYouEachOrder')}
        </div>
        <PackageScanner
          buttonLabel={tr('lab.scanOrderQr')}
          placeholder={tr('lab.ord000123')}
          endpoint="/api/orders/lab/receive"
          onSuccess={refresh}
          formatSuccess={(payload, code) => {
            const o = payload?.order;
            if (payload?.alreadyReceived) {
              return tr('lab.orderAlreadyAtLab', { number: o?.orderNumber || code });
            }
            const tail = payload?.packageAdvanced
              ? tr('lab.packageAdvancedToLab', { number: payload.packageNumber })
              : '';
            return `${tr('lab.orderReceivedAtLab', { number: o?.orderNumber || code })}${tail}`;
          }}
        />
      </Card>
    </>
  );

  const renderAssignTab = () => (
    <Card>
      <SectionTitle><UserPlus size={16} /> {tr('lab.unassignedOrders')}{inbound.length})</SectionTitle>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.7rem' }}>
        {tr('lab.ordersReceivedAtTheLab')}
      </div>
      {inbound.filter(matchSearch).length === 0 ? (
        <Empty>{tr('lab.noUnassignedOrdersWaitingAt')}</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.55rem' }}>
          {inbound.filter(matchSearch).map((o) => (
            <div
              key={o._id}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                gap: '0.75rem', padding: '0.625rem 0.75rem',
                border: '1px solid #e2e8f0', borderRadius: 12, background: '#f8fafc', flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                <strong style={{ color: '#0f172a' }}>{o.orderNumber}</strong>
                <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                  {o.visitor ? [o.visitor.firstName, o.visitor.lastName].filter(Boolean).join(' ') : '—'} · {o.store?.name || '—'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={pickedTech[o._id] || ''}
                  onChange={(e) => setPickedTech((m) => ({ ...m, [o._id]: e.target.value }))}
                  disabled={busy[o._id]}
                  style={{ padding: '0.4rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', minWidth: 170 }}
                  title={tr('lab.whoDoesTheWork')}
                >
                  <option value="">{tr('lab.worksOnIt')}</option>
                  <option value={meId}>{tr('lab.myself')}{user.firstName || 'me'})</option>
                  {technicians
                    .filter((t) => String(t._id) !== meId)
                    .map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.firstName} {t.lastName} ({t.role === 'lab_manager' ? tr('lab.labMgr') : tr('lab.tech')})
                      </option>
                    ))}
                </select>
                {/* Whoever is doing the work is removed from this list, so the
                    two can never be the same person. */}
                <select
                  value={pickedController[o._id] || ''}
                  onChange={(e) => setPickedController((m) => ({ ...m, [o._id]: e.target.value }))}
                  disabled={busy[o._id] || !pickedTech[o._id]}
                  style={{ padding: '0.4rem 0.55rem', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: '0.85rem', minWidth: 170 }}
                  title={tr('lab.whoControlsItMustBe')}
                >
                  <option value="">{tr('lab.controlsIt')}</option>
                  {String(pickedTech[o._id]) !== meId && (
                    <option value={meId}>{tr('lab.myself')}{user.firstName || 'me'})</option>
                  )}
                  {technicians
                    .filter((t) => String(t._id) !== meId && String(t._id) !== String(pickedTech[o._id]))
                    .map((t) => (
                      <option key={t._id} value={t._id}>
                        {t.firstName} {t.lastName} ({t.role === 'lab_manager' ? tr('lab.labMgr') : tr('lab.tech')})
                      </option>
                    ))}
                </select>
                <PrimaryButton
                  onClick={() => assignOrder(o._id, pickedTech[o._id], pickedController[o._id])}
                  disabled={!pickedTech[o._id] || !pickedController[o._id] || busy[o._id]}
                >
                  {busy[o._id] ? tr('lab.assigning') : tr('lab.assign')}
                </PrimaryButton>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );

  const renderActiveTab = () => (
    <Card>
      <SectionTitle><Wrench size={16} /> {tr('lab.myActiveWork')}{active.length})</SectionTitle>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
        {tr('lab.ordersOnYourBenchPause')}
      </div>
      {active.filter(matchSearch).length === 0 ? (
        <Empty>{tr('lab.nothingOnYourBenchYet')}</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          {active.filter(matchSearch).map(renderActiveCard)}
        </div>
      )}
    </Card>
  );

  const renderHoldTab = () => (
    <Card>
      <SectionTitle><Pause size={16} /> {tr('lab.onHold2')}{onHold.length})</SectionTitle>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
        {tr('lab.ordersPausedAcrossTheEntire')}
      </div>
      {onHold.filter(matchSearch).length === 0 ? (
        <Empty>{tr('lab.nothingOnHoldSmoothSailing')}</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          {onHold.filter(matchSearch).map(renderActiveCard)}
        </div>
      )}
    </Card>
  );

  const renderQcTab = () => (
    <Card>
      <SectionTitle><ShieldCheck size={16} /> {tr('lab.control2')}{qc.length})</SectionTitle>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
        {tr('lab.ordersTheLabHasFinished')}
      </div>
      {qc.filter(matchSearch).length === 0 ? (
        <Empty>{tr('lab.controlQueueIsClear')}</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.7rem' }}>
          {qc.filter(matchSearch).map((o) => {
            const lastQc = (o.qcHistory || []).slice(-1)[0];
            const passed = lastQc && lastQc.outcome === 'pass';
            return (
              <div
                key={o._id}
                style={{
                  border: `1px solid ${passed ? '#bbf7d0' : '#fde68a'}`,
                  borderRadius: 14,
                  background: passed
                    ? 'linear-gradient(180deg, #f0fdf4 0%, #ffffff 100%)'
                    : 'linear-gradient(180deg, #fffbeb 0%, #ffffff 100%)',
                  padding: '0.85rem 1rem',
                  display: 'flex', flexDirection: 'column', gap: '0.55rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>{o.orderNumber}</strong>
                    {passed ? (
                      <StatusPill bg="#dcfce7" color="#166534"><CheckCircle2 size={11} /> {tr('lab.controlled')}</StatusPill>
                    ) : (
                      <StatusPill bg="#fef9c3" color="#854d0e"><ShieldCheck size={11} /> {tr('lab.awaitingControl')}</StatusPill>
                    )}
                    {o.technician?.firstName && (
                      <StatusPill bg="#e2e8f0" color="#334155">
                        <UserIcon size={11} /> {tr('lab.builtBy')} {o.technician.firstName} {o.technician.lastName || ''}
                      </StatusPill>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Only the named controller signs off. Everyone else sees
                        whose call it is rather than buttons that would 403. */}
                    {!passed && (isController(o) ? (
                      <>
                        <IconButton
                          onClick={() => { setQcModal({ order: o, outcome: 'fail' }); setQcNotes(''); }}
                          disabled={busy[o._id]}
                          $tone="fail"
                          title={tr('lab.failSendBackForRework')}
                          aria-label={tr('lab.fail')}
                        >
                          <X size={17} />
                        </IconButton>
                        <IconButton
                          onClick={() => { setQcModal({ order: o, outcome: 'pass' }); setQcNotes(''); }}
                          disabled={busy[o._id]}
                          $tone="pass"
                          title={tr('lab.pass')}
                          aria-label={tr('lab.pass')}
                        >
                          <Check size={17} />
                        </IconButton>
                      </>
                    ) : (
                      <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                        {o.controllerName ? tr('lab.controlsThis', { name: o.controllerName }) : tr('lab.awaitingALabManager')}
                      </span>
                    ))}
                    {passed && (
                      <span style={{ color: '#475569', fontSize: '0.82rem' }}>
                        {tr('lab.readyScanItOnThe')}
                      </span>
                    )}
                  </div>
                </div>
                {lastQc?.notes && (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.5rem 0.75rem', fontSize: '0.82rem', color: '#475569' }}>
                    {tr('lab.qcNotes')} {lastQc.notes}
                  </div>
                )}
                <OrderDetails order={o} />
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  const renderHandoffTab = () => (
    <>
      <Card>
        <SectionTitle><Send size={16} /> {tr('lab.handOffToCentral')}</SectionTitle>
        <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.7rem' }}>
          {tr('lab.scanAQcPassedOrder')}
        </div>
        <PackageScanner
          buttonLabel={tr('lab.scanOrderQr')}
          placeholder={tr('lab.ord000123')}
          endpoint="/api/orders/lab/handoff"
          onSuccess={refresh}
          formatSuccess={(payload, code) => {
            const o = payload?.order;
            return tr('lab.orderHandedToCentral', { number: o?.orderNumber || code });
          }}
        />
      </Card>
      <Card>
        <SectionTitle><CheckCircle2 size={16} /> {tr('lab.readyForHandOff')}{handoffReady.length})</SectionTitle>
        {handoffReady.filter(matchSearch).length === 0 ? (
          <Empty>{tr('lab.nothingHasPassedQcYet')}</Empty>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {handoffReady.filter(matchSearch).map((o) => (
              <div
                key={o._id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.75rem', padding: '0.625rem 0.85rem',
                  border: '1px solid #bbf7d0', borderRadius: 12, background: '#f0fdf4', flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>{o.orderNumber}</strong>
                  <span style={{ marginLeft: 8, color: '#475569', fontSize: '0.85rem' }}>
                    {o.visitor ? [o.visitor.firstName, o.visitor.lastName].filter(Boolean).join(' ') : ''}
                    {o.store?.name ? ` · ${tr('lab.forStore', { name: o.store.name })}` : ''}
                  </span>
                </div>
                <StatusPill bg="#dcfce7" color="#166534"><CheckCircle2 size={11} /> {tr('lab.awaitingScan')}</StatusPill>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );

  const renderRecentTab = () => (
    <Card>
      <SectionTitle><History size={16} /> {tr('lab.recent')}{recent.length})</SectionTitle>
      <div style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: '0.75rem' }}>
        {tr('lab.ordersThatHaveLeftThe')}
      </div>
      {recent.filter(matchSearch).length === 0 ? (
        <Empty>{tr('lab.noRecentLabWorkYet')}</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.45rem' }}>
          {recent.filter(matchSearch).map((o) => {
            const customer = o.visitor ? [o.visitor.firstName, o.visitor.lastName].filter(Boolean).join(' ') : '';
            return (
              <div
                key={o._id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: '0.75rem', padding: '0.55rem 0.8rem',
                  border: '1px solid #e2e8f0', borderRadius: 10, background: 'white', flexWrap: 'wrap',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <strong style={{ color: '#0f172a', fontFamily: 'ui-monospace, monospace' }}>{o.orderNumber}</strong>
                  <span style={{ marginLeft: 8, color: '#475569', fontSize: '0.85rem' }}>
                    {customer}
                    {o.store?.name ? ` · ${tr('lab.forStore', { name: o.store.name })}` : ''}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <StatusPill bg="#e0e7ff" color="#3730a3">{tLabOrderStatus(tr, o.status)}</StatusPill>
                  <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>{formatDateTime(o.updatedAt)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );

  const renderTab = () => {
    // A tab switched off for this person falls back to the first one they have.
    switch (tabMeta?.id) {
      case 'receive': return renderReceiveTab();
      case 'assign':  return renderAssignTab();
      case 'active':  return renderActiveTab();
      case 'on_hold': return renderHoldTab();
      case 'qc':      return renderQcTab();
      case 'handoff': return renderHandoffTab();
      case 'recent':  return renderRecentTab();
      case 'waiting':
      default:        return renderWaitingTab();
    }
  };

  const tabMeta = visibleTabs.find((t) => t.id === tab) || visibleTabs[0];

  return (
    <Shell>
      <Sidebar>
        <div className="brand">
          <div className="icon"><FlaskConical size={18} /></div>
          <div>
            <h1>{tr('lab.optiva')}</h1>
            <p>{tr('lab.lab')}</p>
          </div>
        </div>
        <div className="nav">
          {visibleTabs.map((t) => {
            const Icon = t.icon;
            return (
              <NavItem key={t.id} active={tab === t.id} onClick={() => setTab(t.id)}>
                <Icon size={14} /> {t.label}
                {t.count != null && <span className="badge">{t.count}</span>}
          </NavItem>
            );
          })}
        </div>
        <div className="me">
          <div className="av">{(user.firstName || user.email || '?').slice(0, 1).toUpperCase()}</div>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 600, color: 'white', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {user.firstName || user.email || tr('lab.youFallback')}
            </div>
            <div className="role">
              {user.role === 'lab_manager'
                ? tr('lab.roleLabManager')
                : user.role === 'technician'
                  ? tr('lab.roleTechnician')
                  : (user.role || '').replace(/_/g, ' ')}
            </div>
          </div>
          <button className="logout" onClick={handleLogout} title={tr('lab.logout')}><LogOut size={16} /></button>
        </div>
      </Sidebar>

      <Main>
        <PageHeader>
          <div>
            <h2>{tabMeta?.label || tr('lab.lab')}</h2>
            <p>{user.firstName ? tr('lab.welcomeName', { name: user.firstName }) : tr('lab.receiveEdgeReturn')}</p>
          </div>
          <div className="right">
            <SearchBar>
              <Search size={15} color="#94a3b8" />
              <input placeholder={tr('lab.searchOrders')} value={search} onChange={(e) => setSearch(e.target.value)} />
            </SearchBar>
            <GhostButton onClick={refresh} disabled={loading}>
              <RefreshCw size={14} /> {loading ? tr('lab.refreshing') : tr('lab.refresh')}
            </GhostButton>
          </div>
        </PageHeader>

        {notice && (
          <Toast kind={notice.kind}>
            {notice.kind === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
            {notice.message}
          </Toast>
        )}

        {renderTab()}
      </Main>

      {/* ── Hold modal ── */}
      {holdModal && (
        <ModalBackdrop onClick={() => setHoldModal(null)}>
          <ModalShell onClick={(e) => e.stopPropagation()}>
            <div className="head">
              <h3><Pause size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} /> {tr('lab.pauseWorkOn')} {holdModal.order.orderNumber}</h3>
              <button onClick={() => setHoldModal(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            <div className="row">
              <label>{tr('lab.reason2')}</label>
              <select value={holdReasonCode} onChange={(e) => setHoldReasonCode(e.target.value)}>
                {Object.entries(HOLD_REASON_KEYS).map(([code, key]) => (
                  <option key={code} value={code}>{tr(key)}</option>
                ))}
              </select>
            </div>
            <div className="row">
              <label>{tr('lab.notesOptional')}</label>
              <textarea value={holdNotes} onChange={(e) => setHoldNotes(e.target.value)} placeholder={tr('lab.whatHappenedWhoNeedsTo')} />
            </div>
            <div className="actions">
              <GhostButton onClick={() => setHoldModal(null)}>{tr('lab.cancel')}</GhostButton>
              <PrimaryButton onClick={submitHold} disabled={busy[holdModal.order._id]} bg="linear-gradient(135deg, #f59e0b 0%, #d97706 100%)">
                <Pause size={13} /> {busy[holdModal.order._id] ? tr('lab.saving') : tr('lab.pauseWork')}
              </PrimaryButton>
            </div>
          </ModalShell>
        </ModalBackdrop>
      )}

      {/* ── Control modal ── A pass only confirms; there is nothing to explain
           about work that was correct. A fail must say what to redo, or the
           technician gets the job back with no idea what was wrong. */}
      {qcModal && (
        <ModalBackdrop onClick={() => setQcModal(null)}>
          <ModalShell onClick={(e) => e.stopPropagation()}>
            <div className="head">
              <h3>
                {qcModal.outcome === 'pass'
                  ? <><Check size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} /> {tr('lab.passControl')} {qcModal.order.orderNumber}</>
                  : <><X size={16} style={{ verticalAlign: '-2px', marginRight: 6 }} /> {tr('lab.failControl')} {qcModal.order.orderNumber}</>
                }
              </h3>
              <button onClick={() => setQcModal(null)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}><X size={18} /></button>
            </div>
            {qcModal.outcome === 'pass' ? (
              <div className="row">
                <p style={{ margin: 0, fontSize: '0.88rem', color: '#475569' }}>
                  {tr('lab.markThisOrderAsControlled')}
                </p>
              </div>
            ) : (
              <div className="row">
                <label>{tr('lab.whatNeedsReworkingRequired')}</label>
                <textarea
                  value={qcNotes}
                  onChange={(e) => setQcNotes(e.target.value)}
                  placeholder={tr('lab.eGAxisOffBy')}
                />
              </div>
            )}
            <div className="actions">
              <GhostButton onClick={() => setQcModal(null)}>{tr('lab.cancel')}</GhostButton>
              <PrimaryButton
                onClick={submitQc}
                disabled={busy[qcModal.order._id] || (qcModal.outcome === 'fail' && !qcNotes.trim())}
                bg={qcModal.outcome === 'pass'
                  ? 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                  : 'linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)'}
              >
                {qcModal.outcome === 'pass' ? <Check size={13} /> : <X size={13} />}
                {busy[qcModal.order._id] ? tr('lab.saving') : (qcModal.outcome === 'pass' ? tr('lab.confirmPass') : tr('lab.sendBackToManufacturing'))}
              </PrimaryButton>
            </div>
          </ModalShell>
        </ModalBackdrop>
      )}
    </Shell>
  );
};

export default LabDashboard;
